import React, { useState, useEffect, useCallback } from "react";
import Input from "../../components/Input";
import DateInput from "../../components/DateInput";
import { Button } from "../../components/Button";
import Dropdown from "../../components/Dropdown";
import { useDispatch, useSelector } from "react-redux";
import {
  formatLabel,
  reminderIntervalList,
  ROLE_NAMES,
} from "../../uitls/Utils";
import { Card } from "@material-tailwind/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Assignment,
  Business,
  Description,
  People,
  Schedule,
  NotificationsActive,
  CheckCircle,
  ArrowBackIos,
} from "@mui/icons-material";
import {
  filterUsersByDept_and_Role,
  getManagerByDept,
} from "../../api/userApi";
import { getAllDepartment } from "../../api/departmentApi";
import { getTemplatesByDeptId } from "../../api/templateApi";
import { showNotification } from "../../components/Notification";
import {
  assignAppraisalTemplate,
  getUserAssignedAppraisalDetails,
  updateUserAssignedAppraisal,
} from "../../api/appraisalApi";
import {
  resetSelectedAppraisalStates,
  updateMessage,
} from "../appraisal/appraisalSlice";
import { resetTemplateState } from "./templateSlice";
import Loader from "../../components/Loader";

const AddEditAssignTemplate = () => {
  const [templateState, setTemplateState] = useState({
    userIds: [],
    userTimeLine: "",
    template: "",
    businessUnit: {},
    employeeReminder: null,
    finalTimeLine: "",
    managerReminder: null,
    managerTimeLine: "",
    manager: {},
  });
  const [editTemplateId, setEditTemplateId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appraisalState = useSelector((state) => state.appraisalState);
  const { usersByDept_and_role, managerBydept } = useSelector(
    (state) => state.userState
  );
  const { departments } = useSelector((state) => state.departmentState);
  const { templates, loading } = useSelector((state) => state.templateState);
  const { message, error, selectedAppraisal } = useSelector(
    (state) => state.appraisalState
  );
  const {
    success,
    error: errorUpdate,
    loading: loadingUpdate,
  } = useSelector((state) => state.templateState);

  useEffect(() => {
    const id = searchParams.get("id");
    if (id) {
      setEditTemplateId(id);
      dispatch(getUserAssignedAppraisalDetails(id));
    }
    dispatch(getAllDepartment());
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (templateState?.businessUnit?.id) {
      // Only clear template selection if we're not in edit mode
      if (!editTemplateId) {
        setTemplateState((prevState) => ({
          ...prevState,
          template: {},
          manager: {},
          userIds: [],
        }));
      }
      dispatch(getTemplatesByDeptId(templateState.businessUnit.id));
    }
  }, [dispatch, templateState?.businessUnit?.id, editTemplateId]);

  useEffect(() => {
    if (!selectedAppraisal) return;

    const businessUnit = departments?.find(
      (dept) => dept.id === selectedAppraisal?.appraisalTemplate?.businessUnit
    );

    const manager = managerBydept?.find(
      (mng) =>
        mng.userRoleId === ROLE_NAMES.Manager &&
        mng.id === selectedAppraisal?.managerId &&
        !mng.isDeleted
    );

    const newTemplateState = {
      template: selectedAppraisal?.appraisalTemplate,
      businessUnit,
      userIds: [selectedAppraisal?.assignedUser],
      employeeReminder: reminderIntervalList.find(
        (r) => r.value === selectedAppraisal?.userReminderInterval
      ),
      managerReminder: reminderIntervalList.find(
        (r) => r.value === selectedAppraisal?.managerReminderInterval
      ),
      userTimeLine: selectedAppraisal?.userSubmissionEndDate,
      managerTimeLine: selectedAppraisal?.managerReviewEndDate,
      finalTimeLine: selectedAppraisal?.finalReviewEndDate,
      manager,
    };

    setTemplateState((prevState) => ({
      ...prevState,
      ...newTemplateState,
    }));
  }, [selectedAppraisal, departments, managerBydept]); // Added departments and managerBydept to dependencies

  // Update manager when managerBydept is loaded after selectedAppraisal
  useEffect(() => {
    if (!selectedAppraisal || !managerBydept || managerBydept.length === 0)
      return;

    // Only update if manager is not already set
    if (!templateState?.manager?.id) {
      const manager = managerBydept?.find(
        (mng) =>
          mng.userRoleId === ROLE_NAMES.Manager &&
          mng.id === selectedAppraisal?.managerId &&
          !mng.isDeleted
      );

      if (manager) {
        setTemplateState((prevState) => ({
          ...prevState,
          manager,
        }));
      }
    }
  }, [managerBydept, selectedAppraisal]);

  useEffect(() => {
    if (message && !error) {
      showNotification("success", message);

      setTimeout(() => {
        dispatch(resetSelectedAppraisalStates());
        dispatch(resetTemplateState());
        dispatch(updateMessage(""));
        navigate(-1);
        resetStates();
      }, 500);
    } else if (!message && error) {
      showNotification("error", error);
    }
  }, [message, error]);

  useEffect(() => {
    if (success && !errorUpdate) {
      setTimeout(() => {
        dispatch(resetSelectedAppraisalStates());
        dispatch(resetTemplateState());
        navigate(-1);
        resetStates();
      }, 500);
    } else if (!success && errorUpdate) {
      showNotification("error", errorUpdate);
    }
  }, [success, errorUpdate]);

  useEffect(() => {
    if (!templateState?.businessUnit) return;

    dispatch(
      getManagerByDept({
        userRole: ROLE_NAMES.Manager,
        deptId: templateState.businessUnit.id,
      })
    );
  }, [templateState?.businessUnit?.id]);

  useEffect(() => {
    if (templateState?.manager?.id && templateState?.template?.id) {
      dispatch(
        filterUsersByDept_and_Role({
          managerId: templateState.manager.id,
          templateId: templateState?.template?.id,
        })
      );
    }
  }, [templateState?.manager?.id, templateState?.template]);

  // Helper function to validate string fields
  const validateStringField = (key, value) => {
    if (typeof value === "string" && key !== "id" && !value.trim()) {
      return `${formatLabel(key)} is required`;
    }
    return "";
  };

  // Helper function to validate object fields
  const validateObjectField = (key, value) => {
    const objectFields = [
      "template",
      "businessUnit",
      "manager",
      "employeeReminder",
      "managerReminder",
    ];

    if (
      objectFields.includes(key) &&
      (!value || Object.keys(value).length === 0)
    ) {
      return `${formatLabel(key)} is required`;
    }
    return "";
  };

  // Helper function to validate array fields
  const validateArrayField = (key, value) => {
    const arrayFields = ["userIds", "appraisalQuestions"];

    if (arrayFields.includes(key) && (!value || value.length === 0)) {
      return `${formatLabel(key)} is required`;
    }
    return "";
  };

  // Helper function to validate timeline fields
  const validateTimelineField = (key, value) => {
    const timelineFields = ["userTimeLine", "managerTimeLine", "finalTimeLine"];

    if (timelineFields.includes(key) && !value) {
      return `${formatLabel(key)} is required`;
    }
    return "";
  };

  // Helper function to validate question options
  const validateQuestionOptions = (question, qIndex) => {
    const errors = {};

    question.questionOptions.forEach((optionObj, oIndex) => {
      const optionValue = optionObj?.["optionTitle"];
      if (!optionValue || optionValue.trim() === "") {
        errors[`option${qIndex + 1}-${oIndex + 1}`] = `Option ${
          oIndex + 1
        } is required`;
      }
    });

    return errors;
  };

  // Helper function to validate appraisal questions
  const validateAppraisalQuestions = (value) => {
    const errors = {};

    if (Array.isArray(value)) {
      value.forEach((question, qIndex) => {
        if (!question.questionTitle || question.questionTitle.trim() === "") {
          errors[`question${qIndex + 1}`] = `Question ${
            qIndex + 1
          } is required`;
        }

        const optionErrors = validateQuestionOptions(question, qIndex);
        Object.assign(errors, optionErrors);
      });
    }

    return errors;
  };

  // Helper function to validate a single field
  const validateField = (key, value) => {
    // Check string field validation
    const stringError = validateStringField(key, value);
    if (stringError) return stringError;

    // Check object field validation
    const objectError = validateObjectField(key, value);
    if (objectError) return objectError;

    // Check array field validation
    const arrayError = validateArrayField(key, value);
    if (arrayError) return arrayError;

    // Check timeline field validation
    const timelineError = validateTimelineField(key, value);
    if (timelineError) return timelineError;

    return "";
  };

  const validateTemplate = () => {
    const validationErrors = {};
    const keysToCheck = Object.keys(templateState);

    keysToCheck.forEach((key) => {
      const value = templateState[key];

      // Validate basic field
      const fieldError = validateField(key, value);
      if (fieldError) {
        validationErrors[key] = fieldError;
      }

      // Validate appraisal questions if applicable
      if (key === "appraisalQuestions") {
        const questionErrors = validateAppraisalQuestions(value);
        Object.assign(validationErrors, questionErrors);
      }
    });

    setFormErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const initialTemplateState = {
    userIds: [],
    userTimeLine: "",
    template: "",
    businessUnit: {},
    employeeReminder: null,
    finalTimeLine: "",
    managerReminder: null,
    managerTimeLine: "",
    manager: {},
  };

  const resetForm = () => {
    setTemplateState(initialTemplateState);
    setFormErrors({});
  };

  const resetStates = () => {
    setTemplateState({
      id: null,
      templateTitle: "",
      businessUnit: "",
      userIds: "",
      userTimeLine: "",
      managerTimeLine: "",
      reminderTimeInterval: "",
      managerId: null,
      appraisalQuestions: [
        {
          id: null,
          questionTitle: "",
          questionSubTitle: "",
          questionOptions: [
            { id: null, optionTitle: "" },
            { id: null, optionTitle: "" },
          ],
          optionType: 1,
        },
      ],
    });
    setEditTemplateId(null);

    setFormErrors({});
  };

  const handleSaveTemplate = () => {
    if (!validateTemplate()) {
      showNotification("error", "Please fill all the required fields.");
      return;
    }

    const {
      userIds,
      userTimeLine,
      template,
      businessUnit,
      employeeReminder,
      finalTimeLine,
      managerReminder,
      managerTimeLine,
      manager,
    } = templateState;

    const payload = {
      templateId: template?.id,
      assignedUserIds: userIds?.map((u) => u.id),
      userSubmissionEndDate: userTimeLine,
      managerReviewEndDate: managerTimeLine,
      finalReviewEndDate: finalTimeLine,
      businessUnit: businessUnit?.id,
      userReminderInterval: employeeReminder?.value,
      managerReminderInterval: managerReminder?.value,
      managerId: manager?.id,
    };

    if (editTemplateId) {
      dispatch(
        updateUserAssignedAppraisal({ id: editTemplateId, data: payload })
      );
    } else {
      dispatch(assignAppraisalTemplate(payload));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormErrors({ ...formErrors, [name]: "" });
    if (name === "userTimeLine") {
      setTemplateState({
        ...templateState,
        userTimeLine: value,
        managerTimeLine: "",
        finalTimeLine: "",
      });
    } else if (name === "managerTimeLine") {
      const newManagerTimeLine = value;
      setTemplateState({
        ...templateState,
        managerTimeLine: newManagerTimeLine,
        finalTimeLine: "",
      });
    } else if (name === "finalTimeLine") {
      const newFinalTimeLine = value;
      setTemplateState({
        ...templateState,
        finalTimeLine: newFinalTimeLine,
      });
    }
  };

  // Handle date changes from DateInput component
  const handleDateChange = (name, date) => {
    setFormErrors({ ...formErrors, [name]: "" });
    const dateString = date ? date.toISOString().split("T")[0] : "";
    
    if (name === "userTimeLine") {
      setTemplateState({
        ...templateState,
        userTimeLine: dateString,
        managerTimeLine: "",
        finalTimeLine: "",
      });
    } else if (name === "managerTimeLine") {
      setTemplateState({
        ...templateState,
        managerTimeLine: dateString,
        finalTimeLine: "",
      });
    } else if (name === "finalTimeLine") {
      setTemplateState({
        ...templateState,
        finalTimeLine: dateString,
      });
    }
  };

  const handleDropdownChange = useCallback((name, option) => {
    setFormErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    setTemplateState((prevState) => ({
      ...prevState,
      [name]: option,
      ...(name === "businessUnit" && {
        template: {},
        manager: {},
        userIds: [],
      }),
      ...(name === "template" && {
        manager: {},
        userIds: [],
      }),
      ...(name === "manager" && { userIds: [] }),
    }));
  }, []);

  const getMinDate = (date) => {
    return date ? new Date(date) : null;
  };
  const getMinDatePlusOne = (date) => {
    if (!date) return null;
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + 1);
    return newDate;
  };

  // Convert string dates to Date objects for DateInput
  const getDateValue = (dateString) => {
    return dateString ? new Date(dateString) : null;
  };

  // Handle calendar positioning for mobile vs desktop
  useEffect(() => {
    const handleDateInputFocus = (e) => {
      const input = e.target;
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // Mobile: Scroll the input to center of screen to give calendar space
        setTimeout(() => {
          const rect = input.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          const inputCenter = rect.top + rect.height / 2;
          const targetScroll = window.scrollY + inputCenter - windowHeight / 2;

          // Smooth scroll to center the input
          window.scrollTo({
            top: Math.max(0, targetScroll),
            behavior: "smooth",
          });
        }, 100);
      }
    };

    const dateInputs = document.querySelectorAll('input[type="date"]');
    dateInputs.forEach((input) => {
      input.addEventListener("focus", handleDateInputFocus);
    });

    return () => {
      dateInputs.forEach((input) => {
        input.removeEventListener("focus", handleDateInputFocus);
      });
    };
  }, []);
  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      {(loading || appraisalState?.loading) && <Loader />}
      
      <div className="max-w-5xl mx-auto">
        {/* Back Navigation Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate("/admin/assign-template")}
            className="flex items-center gap-2 text-[#135e96] hover:text-[#0b3a5c] font-semibold transition-colors duration-200 group"
          >
            <ArrowBackIos className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
            <span>Back</span>
          </button>
        </div>

        {/* Main Form Card */}
        <Card className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-0 space-y-0">
            
            {/* Step 1: Basic Information */}
            <div className="space-y-4">
              <div className="brand-header flex items-center gap-3 py-3 px-4 rounded-t-lg">
                <div className="relative">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <Business className="text-white" fontSize="small" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white flex items-center justify-center border-2 border-[#135e96] shadow-sm">
                    <span className="text-[#135e96] font-bold text-xs">1</span>
                  </div>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Basic Information</h2>
                  <p className="text-sm text-blue-100">Select business unit and template</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 sm:p-6">
                <Dropdown
                  keyLabel="departmentName"
                  keyValue="id"
                  submitRef={true}
                  defaultValue={templateState?.businessUnit || null}
                  options={departments}
                  handleChange={(option) =>
                    handleDropdownChange("businessUnit", option)
                  }
                  requiredField={true}
                  defaultLabel="Select Business Unit"
                  label="Business Unit"
                  errorMessage={formErrors?.businessUnit}
                  isClearable={true}
                  menuPlacement="bottom"
                />
                <Dropdown
                  submitRef={true}
                  defaultValue={templateState?.template || null}
                  options={templates}
                  handleChange={(option) =>
                    handleDropdownChange("template", option)
                  }
                  keyLabel="templateTitle"
                  keyValue="id"
                  requiredField={true}
                  defaultLabel="Select Template"
                  label="Template"
                  errorMessage={formErrors?.template}
                  isClearable={true}
                  menuPlacement="bottom"
                />
              </div>
            </div>

            {/* Step 2: Assignment */}
            <div className="space-y-4 border-b border-gray-200 last:border-b-0">
              <div className="brand-header flex items-center gap-3 py-3 px-4 sm:px-6">
                <div className="relative">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <People className="text-white" fontSize="small" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white flex items-center justify-center border-2 border-[#135e96] shadow-sm">
                    <span className="text-[#135e96] font-bold text-xs">2</span>
                  </div>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Assignment</h2>
                  <p className="text-sm text-blue-100">Select manager and employees</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 sm:p-6">
                <Dropdown
                  submitRef={true}
                  keyLabel="firstName"
                  keyLabel2="lastName"
                  keyValue="id"
                  defaultValue={templateState?.manager || null}
                  options={managerBydept}
                  handleChange={(option) =>
                    handleDropdownChange("manager", option)
                  }
                  requiredField={true}
                  defaultLabel="Select Manager"
                  label="Manager"
                  multiple={false}
                  errorMessage={formErrors?.manager}
                />
                <Dropdown
                  submitRef={true}
                  keyLabel="firstName"
                  keyLabel2="lastName"
                  keyValue="id"
                  defaultValue={templateState?.userIds || null}
                  options={usersByDept_and_role}
                  handleChange={(option) =>
                    handleDropdownChange("userIds", option)
                  }
                  requiredField={true}
                  defaultLabel="Select Employees"
                  label="Employees"
                  multiple={true}
                  errorMessage={formErrors?.userIds}
                />
              </div>
            </div>

            {/* Step 3: Timeline */}
            <div className="space-y-4 border-b border-gray-200 last:border-b-0">
              <div className="brand-header flex items-center gap-3 py-3 px-4 sm:px-6">
                <div className="relative">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <Schedule className="text-white" fontSize="small" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white flex items-center justify-center border-2 border-[#135e96] shadow-sm">
                    <span className="text-[#135e96] font-bold text-xs">3</span>
                  </div>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Timeline</h2>
                  <p className="text-sm text-blue-100">Set important dates</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 sm:p-6">
                <div className="mb-2">
                  <label className="block text-sm font-semibold mb-1.5 text-gray-700">
                    User Submission End Date
                    <span className="text-red-500 text-base ml-1">*</span>
                  </label>
                  <DateInput
                    id="userTimeLine"
                    selected={getDateValue(templateState.userTimeLine)}
                    onChange={(date) => handleDateChange("userTimeLine", date)}
                    placeholderText="mm/dd/yyyy"
                    dateFormat="MM/dd/yyyy"
                    minDate={new Date()}
                    error={!!formErrors.userTimeLine}
                    calendarClassName="shadow-lg border-2 border-gray-200 rounded-xl"
                    popperClassName="z-50"
                  />
                  {formErrors.userTimeLine && (
                    <div className="mt-0.5 min-h-[0.875rem] flex items-center gap-2 text-xs text-[#ef4444]">
                      <svg
                        className="w-4 h-4 text-red-500 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>{formErrors.userTimeLine}</span>
                    </div>
                  )}
                </div>
                <div className="mb-2">
                  <label className="block text-sm font-semibold mb-1.5 text-gray-700">
                    Manager Review End Date
                    <span className="text-red-500 text-base ml-1">*</span>
                  </label>
                  <DateInput
                    id="managerTimeLine"
                    selected={getDateValue(templateState.managerTimeLine)}
                    onChange={(date) => handleDateChange("managerTimeLine", date)}
                    placeholderText="mm/dd/yyyy"
                    dateFormat="MM/dd/yyyy"
                    minDate={getMinDatePlusOne(getMinDate(templateState.userTimeLine))}
                    error={!!formErrors.managerTimeLine}
                    calendarClassName="shadow-lg border-2 border-gray-200 rounded-xl"
                    popperClassName="z-50"
                  />
                  {formErrors.managerTimeLine && (
                    <div className="mt-0.5 min-h-[0.875rem] flex items-center gap-2 text-xs text-[#ef4444]">
                      <svg
                        className="w-4 h-4 text-red-500 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>{formErrors.managerTimeLine}</span>
                    </div>
                  )}
                </div>
                <div className="mb-2">
                  <label className="block text-sm font-semibold mb-1.5 text-gray-700">
                    Final Review End Date
                    <span className="text-red-500 text-base ml-1">*</span>
                  </label>
                  <DateInput
                    id="finalTimeLine"
                    selected={getDateValue(templateState.finalTimeLine)}
                    onChange={(date) => handleDateChange("finalTimeLine", date)}
                    placeholderText="mm/dd/yyyy"
                    dateFormat="MM/dd/yyyy"
                    minDate={getMinDatePlusOne(getMinDate(templateState.managerTimeLine))}
                    error={!!formErrors.finalTimeLine}
                    calendarClassName="shadow-lg border-2 border-gray-200 rounded-xl"
                    popperClassName="z-50"
                  />
                  {formErrors.finalTimeLine && (
                    <div className="mt-0.5 min-h-[0.875rem] flex items-center gap-2 text-xs text-[#ef4444]">
                      <svg
                        className="w-4 h-4 text-red-500 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>{formErrors.finalTimeLine}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Step 4: Reminders */}
            <div className="space-y-4 border-b border-gray-200 last:border-b-0">
              <div className="brand-header flex items-center gap-3 py-3 px-4 sm:px-6">
                <div className="relative">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <NotificationsActive className="text-white" fontSize="small" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white flex items-center justify-center border-2 border-[#135e96] shadow-sm">
                    <span className="text-[#135e96] font-bold text-xs">4</span>
                  </div>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Reminder Settings</h2>
                  <p className="text-sm text-blue-100">Configure notification intervals</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 sm:p-6">
                <Dropdown
                  submitRef={true}
                  defaultValue={templateState?.employeeReminder || null}
                  options={reminderIntervalList}
                  handleChange={(option) =>
                    handleDropdownChange("employeeReminder", option)
                  }
                  requiredField={true}
                  defaultLabel="Select Interval"
                  label="Employee Reminder"
                  multiple={false}
                  errorMessage={formErrors?.employeeReminder}
                />
                <Dropdown
                  submitRef={true}
                  defaultValue={templateState?.managerReminder || null}
                  options={reminderIntervalList}
                  handleChange={(option) =>
                    handleDropdownChange("managerReminder", option)
                  }
                  requiredField={true}
                  defaultLabel="Select Interval"
                  label="Manager Reminder"
                  multiple={false}
                  errorMessage={formErrors?.managerReminder}
                />
              </div>
            </div>

          </div>

          {/* Action Buttons */}
          <div className="px-6 sm:px-8 py-5 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-3">
            <Button
              variant="outline"
              value="Reset the form"
              handleClick={resetForm}
              disabled={loading || appraisalState?.loading || loadingUpdate}
              className="w-full sm:w-auto min-w-[140px]"
            />
            <Button
              variant="primary"
              value={editTemplateId ? "Update Appraisal" : "Save Appraisal"}
              disabled={
                loading || appraisalState?.loading || loadingUpdate || false
              }
              handleClick={handleSaveTemplate}
              loading={loading || appraisalState?.loading || loadingUpdate}
              className="w-full sm:w-auto min-w-[160px]"
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AddEditAssignTemplate;
